import React, { useState, useEffect, useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button, CustomSelect, Label } from '@redpanda/ui';
import { bookingApi } from '../services/api';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface RescheduleBookingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newDate: string) => void;
  booking: {
    id: string;
    storeId: string;
    serviceId: string;
    staffId?: string;
    date: string;
  };
}

const RescheduleBookingDialog: React.FC<RescheduleBookingDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  booking,
}) => {
  const { t } = useTranslation(['pages', 'translation']);

  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // 重置表單（同步設置初始值）
  useEffect(() => {
    if (isOpen) {
      const currentDate = new Date(booking.date);
      const dateStr = currentDate.toISOString().split('T')[0];
      setSelectedDate(dateStr);
      setSelectedTime(currentDate.toTimeString().substring(0, 5));
      loadAvailableSlots(dateStr);
    } else {
      // 關閉時重置
      setSelectedDate('');
      setSelectedTime('');
      setAvailableSlots([]);
      setSlotsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, booking.date]);

  const loadAvailableSlots = async (date: string) => {
    setSlotsLoading(true);
    try {
      const response = await bookingApi.getAvailableSlots({
        storeId: booking.storeId,
        serviceId: booking.serviceId,
        date,
        staffId: booking.staffId,
      });
      setAvailableSlots(response.data.availableSlots || []);
    } catch (error: any) {
      toast.error(t('dialogs.reschedule.noSlotsError', { ns: 'pages' }));
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    setSelectedTime('');
    loadAvailableSlots(date);
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error(t('dialogs.reschedule.selectDateTime', { ns: 'pages' }));
      return;
    }

    setLoading(true);
    try {
      // ✅ 修正時區問題：將本地時間轉換為 UTC ISO 字串
      const localDateTime = `${selectedDate}T${selectedTime}:00`;
      const localDate = new Date(localDateTime);
      const utcISOString = localDate.toISOString();
      await onSuccess(utcISOString);
      onClose();
    } catch (error) {
      // Error handled in parent
    } finally {
      setLoading(false);
    }
  };

  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // 計算未來 30 天的日期選項 (使用 useMemo 避免重複計算導致重新渲染)
  const dateOptions = useMemo(() => {
    return Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      const formattedDate = date.toLocaleDateString('zh-TW', {
        month: '2-digit',
        day: '2-digit',
        weekday: 'short',
      });
      return {
        value: dateStr,
        label: formattedDate,
      };
    });
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
      onMouseDown={handleBackgroundClick}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 標題列 */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('dialogs.reschedule.title', { ns: 'pages' })}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* 表單內容 */}
        <div className="p-6 space-y-6">
          {/* 日期選擇 */}
          <div>
            <Label htmlFor="selectedDate">{t('dialogs.reschedule.selectDate', { ns: 'pages' })}</Label>
            <CustomSelect
              id="selectedDate"
              value={selectedDate}
              onChange={(value) => handleDateChange(value)}
              placeholder={t('dialogs.reschedule.selectDate', { ns: 'pages' })}
              options={dateOptions}
            />
          </div>

          {/* 時間選擇 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('dialogs.reschedule.selectTime', { ns: 'pages' })}
            </label>
            {!selectedDate ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {t('dialogs.reschedule.selectDate', { ns: 'pages' })}
              </div>
            ) : slotsLoading ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {t('dialogs.reschedule.loading', { ns: 'pages' })}
              </div>
            ) : availableSlots.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {t('dialogs.reschedule.noSlots', { ns: 'pages' })}
              </div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {availableSlots.map((time) => (
                  <button
                    key={time}
                    onClick={() => setSelectedTime(time)}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedTime === time
                        ? 'bg-brand-600 text-white'
                        : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 操作按鈕 */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-slate-700">
            <Button
              type="button"
              onClick={onClose}
              variant="secondary"
              disabled={loading}
            >
              {t('dialogs.reschedule.cancel', { ns: 'pages' })}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !selectedDate || !selectedTime}
              className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
            >
              {loading ? t('dialogs.reschedule.processing', { ns: 'pages' }) : t('dialogs.reschedule.confirm', { ns: 'pages' })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RescheduleBookingDialog;
